import { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { Box, Button, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import FilterBtn from "../../../Assets/Icons/btn-filters.png";
import FilterBtnUp from "../../../Assets/Icons/btn-filters-up.png";
import { useNotification } from "../../../Hooks/useNotification";
import { useApiErrorHandler } from "../../../Hooks/useApiErrorHandler";
import CustomLoadingOverlay from "../../../Components/CustomLoadingOverlay";
import { createFilterHandler } from "../../../Utils/FilterUtils";
import httpclient from "../../../Utils";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import EditScheduleDialog from "./EditConfirmationDialog"; // Import the edit dialog
import AddDeliveryTruckScheduleDialog from "./AddDeliveryTruckScheduleDialog";
import PreviewScheduleDialog from "./PreviewScheduleDialog";
import { getColumns } from "./columns";
import Filter from "./Filter";

const INITIAL_FILTER_STATE = {
  group_name: "",
  time: "",
};

const fieldMappings = {
  group_name: ["group_name", "$eq"],
  time: ["time", "$eq"],
};

export default function DeliveryTruckSchedule() {
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_FILTER_STATE);
  const [submittedData, setSubmittedData] = useState({});
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false); // Add edit dialog state
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Add editing state
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [sortModel, setSortModel] = useState([]);

  const showNotification = useNotification();
  const handleApiError = useApiErrorHandler();

  const fetchData = async (params) => {
    setLoading(true);
    try {
      const response = await httpclient.get("/delivery-truck-schedule", {
        params: {
          page: params.page + 1,
          per_page: params.pageSize,
          ...params.filters,
        },
      });
      // console.log("response data check", response.data)
       setApiResponse(response.data);
      if (response.data.status === 200) {
       const mappedData = response.data.data.map((item) => ({
  id: item.id,
  group_name: item.group_name,
  time: item.time,
  created_at: item.created_at,
  created_by: item.created_by,
  selected_bays: item.selected_bays || [], 
   allow_edit: item.allow_edit,           // Add this
        allow_delete: item.allow_delete,       // Add this
        allow_status_update: item.allow_status_update
}));
        
        setData(mappedData);
        setRowCount(response.data.meta.total);
       
        
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // Initial data fetch on component mount
  useEffect(() => {
    fetchData({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      filters: submittedData,
    });
  }, []);

  const handleDeleteSchedule = async () => {
    setIsDeleting(true);
    
    try {
      const response = await httpclient.post(`/delivery-truck-schedule/delete/${selectedSchedule.id}`);
      
      if (response.data.status === 200) {
        showNotification("success", response.data.message);
        setOpenDeleteDialog(false);
        setSelectedSchedule(null);
        fetchData({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          filters: submittedData,
        });
      } else {
        showNotification("info", response.data.message);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditSchedule = async (updatedData) => {
    setIsEditing(true);
    
    try {
      const response = await httpclient.post(
        `/delivery-truck-schedule/update/${selectedSchedule.id}`, 
        updatedData
      );
      
      if (response.data.status === 200) {
        showNotification("success", response.data.message);
        setOpenEditDialog(false);
        setSelectedSchedule(null);
        fetchData({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          filters: submittedData,
        });
      } else {
        showNotification("info", response.data.message);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsEditing(false);
    }
  };

  const handleChangeFilter = (field) => (event, value) => {
    setFilterData((prev) => ({
      ...prev,
      [field]: value || (event && event.target.value) || "",
    }));
  };

  const handleFilter = createFilterHandler({
    setLoading,
    setSubmittedData,
    setResults: (results) => {
      const mappedResults = results.map((item) => ({
        id: item.id,
        group_name: item.group_name,
        time: item.time,
        created_at: item.created_at,
        created_by: item.created_by,
      }));
      setData(mappedResults);
    },
    showNotification,
    handleApiError,
    httpClient: httpclient,
    endpoint: `/delivery-truck-schedule`,
    fieldMappings,
    setRowCount,
    refresh: (params) => fetchData(params),
  });

  const onFilterClick = () => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(filterData);
  };

  const handleRemove = (field) => {
    const newFilterData = { ...filterData };
    const newSubmittedData = { ...submittedData };

    newFilterData[field] = "";
    delete newSubmittedData[field];
    
    setFilterData(newFilterData);
    setSubmittedData(newSubmittedData);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(newFilterData);
  };

  const handleClearFilters = () => {
    setFilterData(INITIAL_FILTER_STATE);
    setSubmittedData({});
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    handleFilter(INITIAL_FILTER_STATE);
  };

  const handleAddSchedule = () => {
    setOpenAddDialog(true);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
    fetchData({
      page: newModel.page,
      pageSize: newModel.pageSize,
      filters: submittedData,
    });
  };

  const columns = getColumns(setSelectedSchedule, setOpenDeleteDialog, setOpenEditDialog,setOpenPreviewDialog,apiResponse );

  const deleteMessage = selectedSchedule ? (
    <>
      Are you sure you want to delete truck delivery schedule for group{" "}
      <strong>{selectedSchedule.group_name}</strong> at time{" "}
      <strong>{selectedSchedule.time}</strong>?
    </>
  ) : (
    "Are you sure you want to delete this truck delivery schedule?"
  );

  return (
    <>
      <Box sx={{ width: "100%", padding: 2 }}>
        <Box
          sx={{
            position: "relative",
            width: "100%",
            backgroundColor: "#fff",
            padding: "24px 16px",
            borderRadius: "8px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Delivery Truck Schedule
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >

              {
                apiResponse && apiResponse.allow_add ===1 && (

               
              <Button
                variant="contained"
                size="large"
                sx={{
                  borderRadius: "4px",
                  marginRight: "16px",
                  
                  backgroundColor: "#7152F3",
                  "&:hover": {
                    backgroundColor: "#5c34fa",
                  },
                }}
                startIcon={<AddIcon sx={{ marginRight: 0 }} />}
                onClick={handleAddSchedule}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                    
                  }}
                >
                  Add New Schedule
                </Typography>
              </Button>
               )
              }
              <img
                src={filterOpen ? FilterBtnUp : FilterBtn}
                alt="Filter"
                style={{ cursor: "pointer" }}
                onClick={() => setFilterOpen(!filterOpen)}
              />
            </Box>
          </Box>

          <Filter
            filterOpen={filterOpen}
            filterData={filterData}
            handleChangeFilter={handleChangeFilter}
            handleFilter={onFilterClick}
            handleClearFilters={handleClearFilters}
            handleRemove={handleRemove}
            submittedData={submittedData}
          />

          <DataGrid
          style= {{marginTop : "25px"}}
            rows={data || []}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            loading={loading}
            slots={{
              loadingOverlay: CustomLoadingOverlay,
            }}
            sx={{
              "& .MuiDataGrid-cell": {
                fontSize: "14px",
                fontWeight: 500,
                color: "#48505E",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#F8F9FA",
                color: "#1A1C1E",
                fontWeight: 600,
              },
            }}
            paginationMode="server"
            sortingMode="server"
            onSortModelChange={handleSortModelChange}
            sortModel={sortModel}
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[20, 50, 100]}
          />
        </Box>
      </Box>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        handleClose={() => setOpenDeleteDialog(false)}
        title="Delete Schedule"
        message={deleteMessage}
        onConfirm={handleDeleteSchedule}
        loading={isDeleting}
      />
      
      {/* Edit Dialog */}
      <EditScheduleDialog
        open={openEditDialog}
        handleClose={() => {
          setOpenEditDialog(false);
          setSelectedSchedule(null);
        }}
        schedule={selectedSchedule}
        onConfirm={handleEditSchedule}
        loading={isEditing}
      />
      
      {/* Add Dialog Component */}
      <AddDeliveryTruckScheduleDialog
        open={openAddDialog}
        handleClose={() => setOpenAddDialog(false)}
        refresh={() => fetchData({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          filters: submittedData,
        })}
      />
      <PreviewScheduleDialog
  open={openPreviewDialog}
  handleClose={() => setOpenPreviewDialog(false)}
  scheduleId={selectedSchedule?.id}
/>
    </>
  );
}